import { appApi, idTag, orgTag } from '@/app/redux/appApi';
import {
  CreateLessonPlanInput,
  LessonPlan,
  UpdateLessonPlanInput,
} from '@/common/types/lessonPlan';
import api from '@/db/apiClient';

const lessonPlansApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getLessonPlansForOrganisation: build.query<LessonPlan[], string>({
      query: (orgId) =>
        api.asEducator.getLessonPlansForOrganisation(orgId),
      providesTags: (result, _err, orgId) =>
        result
          ? [
              orgTag('LessonPlans', orgId),
              ...result.map((lp) => idTag('LessonPlans', lp.id)),
            ]
          : [orgTag('LessonPlans', orgId)],
    }),

    createLessonPlan: build.mutation<LessonPlan, CreateLessonPlanInput>({
      query: (data) => api.asEducator.createLessonPlan(data),
      invalidatesTags: (_res, _err, data) => [
        orgTag('LessonPlans', data.organisationId),
      ],
    }),

    updateLessonPlan: build.mutation<
      null,
      { orgId: string; id: string; updates: UpdateLessonPlanInput }
    >({
      query: async ({ id, updates }) => {
        await api.asEducator.updateLessonPlan(id, updates);
        return null;
      },
      onQueryStarted: async (
        { orgId, id, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          lessonPlansApi.util.updateQueryData(
            'getLessonPlansForOrganisation',
            orgId,
            (draft) => {
              const lp = draft.find((x) => x.id === id);
              if (lp) Object.assign(lp, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteLessonPlan: build.mutation<null, { orgId: string; id: string }>({
      query: async ({ id }) => {
        await api.asEducator.deleteLessonPlan(id);
        return null;
      },
      onQueryStarted: async (
        { orgId, id },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          lessonPlansApi.util.updateQueryData(
            'getLessonPlansForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.findIndex((lp) => lp.id === id);
              if (idx !== -1) draft.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default lessonPlansApi;
export const {
  useGetLessonPlansForOrganisationQuery,
  useCreateLessonPlanMutation,
  useUpdateLessonPlanMutation,
  useDeleteLessonPlanMutation,
} = lessonPlansApi;
